import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import SplashScreenContext from 'contexts/SplashScreenContext';
import {
  getOtaBootState,
  isOtaBootActive,
  OtaBootOutcome,
  retryOtaBoot,
  startOtaBoot,
  subscribeOtaBoot
} from 'services/otaBoot';
import { clearSplashContinuation, markSplashContinuation } from 'services/splashContinuity';

import OfflineLaunchScreen from 'components/OfflineLaunchScreen';

/**
 * Labels for the phases that get a visible progress readout.
 *
 * Deliberately NOTHING for 'idle' and 'checking'. Those two run on every cold
 * launch, including the overwhelming majority with no update to fetch, and the
 * acceptance criterion is that a no-update boot is visually unchanged with no
 * update UI at all. An earlier version labelled them "Checking for updates"
 * and drew a 0% bar, so every launch flashed update UI for a check that almost
 * always finds nothing. Without a label the gate still holds, showing the
 * ordinary branded splash exactly as before this feature existed.
 */
const LABELS: Record<string, string> = {
  downloading: 'Downloading update',
  applying: 'Installing update'
};

/**
 * Holds the app on the branded splash while the cold-launch flow runs, renders
 * the download progress into that splash, and shows the offline screen instead
 * when the launch found no network at all.
 *
 * Mounted between SplashScreenProvider and App so nothing below it renders
 * until the flow resolves. That is what keeps a download off live gameplay
 * traffic: the socket only connects when Lobby or Game mounts and calls
 * setGameType, so gating what renders is enough — no change to
 * ConnectionManager needed.
 *
 * On web, startOtaBoot resolves without doing any work, so children render on
 * the first commit and boot is unchanged. A build pinned with
 * REACT_APP_OTA_ENABLED=false skips the UPDATE but still gets the connectivity
 * verdict, since those pinned manual builds are what this gets tested on.
 */
const OtaBootGate = ({ children }: PropsWithChildren) => {
  const { showSplash, hideSplash } = useContext(SplashScreenContext);
  // The flow is kicked off at module scope in index.tsx, so by the time this
  // mounts it may already be finished — seed from the live state rather than
  // assuming 'idle', or a no-update boot would flash a gate it never needed.
  const [state, setState] = useState(getOtaBootState);
  const [released, setReleased] = useState(
    () => !isOtaBootActive() || getOtaBootState().status === 'done'
  );
  // Seeded the same way, and for a sharper reason: the flow can settle as
  // 'offline' before this ever mounts, and that outcome deliberately does not
  // report 'done', so seeding only from 'done' above would release the gate
  // into the very lobby the offline screen exists to keep the player out of.
  const [offline, setOffline] = useState(() => getOtaBootState().status === 'offline');
  const [retrying, setRetrying] = useState(false);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const settle = useCallback((outcome: OtaBootOutcome) => {
    if (!mounted.current) return;
    // Reaching here at all proves the page was NOT replaced, so any
    // continuation marked below is never going to be used. Drop it, or the
    // next splash this session would skip its intro for a reload that never
    // happened.
    clearSplashContinuation();
    // 'offline' is the ONLY outcome that does not release, and it is safe to
    // hold on precisely because the screen it holds on has a Retry the player
    // can press. That is the difference from the old 'applying' outcome, which
    // held the gate on a progress bar with no way out.
    if (outcome === 'offline') {
      setOffline(true);
      return;
    }
    setOffline(false);
    setReleased(true);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeOtaBoot(setState);
    // Idempotent: returns the in-flight promise if index.tsx already started it.
    //
    // The hold for a SUCCESSFUL apply is not an outcome at all: a successful
    // set() replaces the page, so the service never returns and this callback
    // never runs. Encoding that hold as an 'applying' return value instead is
    // what stranded players on "Installing update 100%" forever when set()
    // failed, since the gate refused to release for it.
    startOtaBoot().then(settle);
    return unsubscribe;
  }, [settle]);

  const retry = useCallback(() => {
    setRetrying(true);
    retryOtaBoot().then((outcome) => {
      if (!mounted.current) return;
      setRetrying(false);
      settle(outcome);
    });
  }, [settle]);

  // A retry that finds a network hands the screen over to the splash, but only
  // once there is something worth showing there. Checking is over in
  // milliseconds, so swapping screens for it would just be a flicker; a
  // download is the long wait, and it has a progress bar.
  const showOffline = offline && state.status !== 'downloading' && state.status !== 'applying';

  // Layout, not passive: the splash has to be committed before the browser
  // paints, or the first frame is an empty (white) body while this effect is
  // still queued.
  useLayoutEffect(() => {
    // The offline screen covers the whole viewport itself, so the splash
    // stands down for it rather than the two stacking.
    if (released || showOffline) {
      hideSplash('otaBoot');
      return;
    }
    const label = LABELS[state.status];
    const detail = label ? { label, percent: state.percent } : undefined;
    showSplash('otaBoot', 'full', detail);

    // Hand the splash to the page on the other side of the apply, carrying
    // what is on screen right now. The apply replaces the document, so the
    // next page mounts a brand new SplashScreen: without this it re-rolls the
    // quip, replays the logo fade, and drops this row, and one continuous
    // wait looks like two screens. Written from a LAYOUT effect so it lands
    // before set() can tear the page down.
    if (state.status === 'applying') markSplashContinuation(detail);
  }, [released, showOffline, state.status, state.percent, showSplash, hideSplash]);

  useEffect(() => () => hideSplash('otaBoot'), [hideSplash]);

  if (showOffline) return <OfflineLaunchScreen onRetry={retry} busy={retrying} />;

  return released ? <>{children}</> : null;
};

export default OtaBootGate;
